import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Paper,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import LinhaTempoLogs from './components/LinhaTempoLogs';
import PainelDetalheLog from './components/PainelDetalheLog';
import { ILogPremium, ITarefaItem } from './types';
import { LogsApi } from './services/logsApi';
import { CentralBackupsApi } from './services/centralBackupsApi';

interface VisualizadorLogsProps {
  clienteID: string;
  token: string;
  tarefaAgendadaIDInicial?: string | null;
  onLimparTarefaAgendadaIDInicial?: () => void;
}

export const VisualizadorLogs: React.FC<VisualizadorLogsProps> = ({
  clienteID,
  token,
  tarefaAgendadaIDInicial,
  onLimparTarefaAgendadaIDInicial
}) => {
  const theme = useTheme();
  const [logs, setLogs] = useState<ILogPremium[]>([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [carregandoMais, setCarregandoMais] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Dropdown de tarefas
  const [tarefasDisponiveis, setTarefasDisponiveis] = useState<ITarefaItem[]>([]);

  // Filtros
  const [tarefaSelecionadaID, setTarefaSelecionadaID] = useState<string>('Todos');
  const [severidade, setSeveridade] = useState<string>('Todos');
  const [filtroBusca, setFiltroBusca] = useState('');
  const [buscaAtiva, setBuscaAtiva] = useState('');
  const [dataInicial, setDataInicial] = useState('');
  const [dataFinal, setDataFinal] = useState('');

  const limit = 20;

  // Log Selecionado
  const [selecionadoLog, setSelecionadoLog] = useState<ILogPremium | null>(null);

  // Carregar tarefas para preencher o filtro
  useEffect(() => {
    CentralBackupsApi.obterTarefas(clienteID, token)
      .then((data) => {
        const itens = data.items.map(t => ({ id: t.tarefaAgendadaID, nome: t.nome }));
        setTarefasDisponiveis(itens);
      })
      .catch((err) => {
        console.error("Erro ao carregar dropdown de tarefas nos logs", err);
      });
  }, [clienteID, token]);

  // Sincronizar com tarefa selecionada externamente
  useEffect(() => {
    if (tarefaAgendadaIDInicial) {
      setTarefaSelecionadaID(tarefaAgendadaIDInicial);
      setLogs([]);
      setSelecionadoLog(null);
    }
  }, [tarefaAgendadaIDInicial]);

  const carregarLogs = useCallback((offset = 0) => {
    const isMais = offset > 0;
    if (isMais) {
      setCarregandoMais(true);
    } else {
      setCarregando(true);
      setErro(null);
    }

    // Converter datas YYYY-MM-DD para ISO
    const dataInicialISO = dataInicial ? `${dataInicial}T00:00:00.000Z` : undefined;
    const dataFinalISO = dataFinal ? `${dataFinal}T23:59:59.999Z` : undefined;

    const queryParams = {
      q: buscaAtiva || undefined,
      severidade: severidade === 'Todos' ? undefined : severidade,
      dataInicial: dataInicialISO,
      dataFinal: dataFinalISO,
      limit,
      offset
    };

    // Decidir se carrega logs da tarefa específica ou do cliente inteiro
    let requisicao;
    if (tarefaSelecionadaID && tarefaSelecionadaID !== 'Todos') {
      requisicao = LogsApi.obterLogsTarefa(tarefaSelecionadaID, token, queryParams);
    } else {
      requisicao = LogsApi.obterLogsCliente(clienteID, token, queryParams);
    }

    requisicao
      .then((data) => {
        if (isMais) {
          setLogs((prev) => [...prev, ...data.items]);
          setCarregandoMais(false);
        } else {
          setLogs(data.items);
          setTotal(data.total);
          setCarregando(false);
          // Auto-seleciona o primeiro log se houver
          if (data.items.length > 0) {
            setSelecionadoLog(data.items[0]);
          } else {
            setSelecionadoLog(null);
          }
        }
      })
      .catch((err) => {
        console.error("Erro ao carregar logs", err);
        setErro("Não foi possível obter a lista de logs.");
        setCarregando(false);
        setCarregandoMais(false);
      });
  }, [clienteID, token, tarefaSelecionadaID, severidade, dataInicial, dataFinal, buscaAtiva, limit]);

  // Carregar dados iniciais ou quando mudar filtros que disparam automático
  useEffect(() => {
    carregarLogs(0);
  }, [carregarLogs]);

  const handleLidarComBusca = (e: React.FormEvent) => {
    e.preventDefault();
    setBuscaAtiva(filtroBusca);
  };

  const limparFiltros = () => {
    setFiltroBusca('');
    setBuscaAtiva('');
    setTarefaSelecionadaID('Todos');
    setSeveridade('Todos');
    setDataInicial('');
    setDataFinal('');
    if (onLimparTarefaAgendadaIDInicial) {
      onLimparTarefaAgendadaIDInicial();
    }
  };

  const temMais = logs.length < total;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 3 }}>
        Central de Logs Premium
      </Typography>

      {/* Barra de Filtros */}
      <Paper
        elevation={0}
        component="form"
        onSubmit={handleLidarComBusca}
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? '#0F172A' : 'rgba(0, 0, 0, 0.02)',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '12px',
          p: 2.5,
          mb: 4
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3.5}>
            <TextField
              placeholder="Buscar por termos no log..."
              value={filtroBusca}
              onChange={(e) => setFiltroBusca(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                sx: { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.4)' : 'rgba(255, 255, 255, 0.8)' }
              }}
            />
          </Grid>

          <Grid item xs={6} sm={4} md={2.2}>
            <TextField
              select
              label="Tarefa"
              value={tarefaSelecionadaID}
              onChange={(e) => setTarefaSelecionadaID(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ sx: { color: 'text.secondary' } }}
            >
              <MenuItem value="Todos">Todas as Tarefas</MenuItem>
              {tarefasDisponiveis.map((tarefa) => (
                <MenuItem key={tarefa.id} value={tarefa.id}>
                  {tarefa.nome}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6} sm={4} md={1.8}>
            <TextField
              select
              label="Severidade"
              value={severidade}
              onChange={(e) => setSeveridade(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ sx: { color: 'text.secondary' } }}
            >
              <MenuItem value="Todos">Todos</MenuItem>
              <MenuItem value="erro">Erro</MenuItem>
              <MenuItem value="alerta">Alerta</MenuItem>
              <MenuItem value="sucesso">Sucesso</MenuItem>
              <MenuItem value="informativo">Informativo</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6} sm={3} md={1.8}>
            <TextField
              type="date"
              label="Data Inicial"
              value={dataInicial}
              onChange={(e) => setDataInicial(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true, sx: { color: 'text.secondary' } }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={1.8}>
            <TextField
              type="date"
              label="Data Final"
              value={dataFinal}
              onChange={(e) => setDataFinal(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true, sx: { color: 'text.secondary' } }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={0.9} sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              onClick={limparFiltros}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                color: 'text.secondary',
                width: '40px',
                height: '40px',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(42, 81, 247, 0.04)',
                  color: 'primary.main'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Paper>

      {/* Erro */}
      {erro && (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body1" color="error" sx={{ mb: 2 }}>
            {erro}
          </Typography>
          <Button variant="contained" onClick={() => carregarLogs(0)}>
            Tentar Novamente
          </Button>
        </Box>
      )}

      {/* Visualizador de Timeline e Detalhes Lado a Lado */}
      {!erro && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={7.5}>
            {carregando ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <LinhaTempoLogs
                logs={logs}
                selecionadoLog={selecionadoLog}
                onSelecionarLog={(log) => setSelecionadoLog(log)}
                temMais={temMais}
                carregandoMais={carregandoMais}
                onCarregarMais={() => carregarLogs(logs.length)}
              />
            )}
          </Grid>
          <Grid item xs={12} md={4.5}>
            <PainelDetalheLog log={selecionadoLog} />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default VisualizadorLogs;
